/**
 * /formations-2-roues — Hub des trames pédagogiques.
 *
 * Reproduit l'esprit de l'infographie CODE 2 ROUES (3 blocs formation)
 * en version dark cohérente avec le reste du site, avec accès direct
 * aux 3 trames détaillées (Plateau A1/A2, Formation 125, Passerelle A2→A)
 * et présentation de la méthode pédagogique commune + niveaux de
 * progression + logique de remédiation.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpenCheck, ChevronRight, Home, Bike, Compass, Award,
  Sparkles, Target, HelpCircle, GraduationCap, AlertTriangle, Wind,
  CheckCircle2, Wrench, Eye, MessageCircle, ArrowRight, Clock,
  Building2, Map as MapIcon,
} from 'lucide-react';
import {
  METHODE_BASELINE, CINQ_QUESTIONS, NIVEAUX_PROGRESSION,
  PRINCIPE_REMEDIATION, OUVERTURE_REFERENTIEL,
} from '../data/pedagogieCommune';

const FORMATIONS = [
  {
    id: 'plateau',
    title: 'Formation Plateau A1 / A2',
    subtitle: 'De la découverte à la maîtrise',
    phase: '3 phases · P1 · P2 · P3',
    color: '#e11d48', // rose (accent CODE 2 ROUES)
    icon: Bike,
    route: '/plateau-progression',
    ctaLabel: 'Ouvrir la trame Plateau',
    steps: [
      { code: 'P1', title: 'Découvrir et ressentir',   items: ['Position sur la moto', 'Démarrage et arrêt', 'Patinage avec/sans frein arrière', 'Braquage', 'Montée & rétrogradation', 'Découverte du lent', 'Demi-tour à basse vitesse'] },
      { code: 'P2', title: 'Coordonner et préciser',    items: ['Lent — objectif 14 s', 'Demi-tour arrêté G/D', 'Demi-tour lancé', 'Répartition du freinage'] },
      { code: 'P3', title: 'Maîtriser et enchaîner',    items: ['Contre-braquage & slalom à 40 km/h', 'Freinage d’urgence', 'Évitement', 'Demi-tour avec passager', 'Assemblage du parcours', 'Examen blanc'] },
    ],
  },
  {
    id: 'formation-125',
    title: 'Formation 125',
    subtitle: 'Une journée pour comprendre, maîtriser et circuler',
    phase: '7 h · Adaptée au niveau et à l’expérience',
    color: '#0891b2', // cyan-600
    icon: Compass,
    route: '/formation-125-progression',
    ctaLabel: 'Ouvrir la trame 125',
    steps: [
      { code: '1', title: 'Théorie · ≈ 1 h à 1 h 30',                 icon: Building2, items: ['Aspects administratifs', 'Équipements', 'Vulnérabilité & visibilité', 'Accidentologie & accidents-types', 'Stabilité, distances, intersections, virages'] },
      { code: '2', title: 'Hors circulation · ≈ 2 h 30 à 3 h',        icon: MapIcon,   items: ['Découverte des commandes & position', 'Béquillage, déplacement moteur arrêté', 'Démarrage/arrêt · patinage · braquage', 'Rapports · arrêt-départ en tournant', 'Demi-tour · contre-braquage', 'Freinage d’urgence · évitement · passager'] },
      { code: '3', title: 'En circulation · ≈ 3 h',                    icon: Bike,      items: ['Théorie de la route & position sur la chaussée', 'Changement de direction', 'Intersections & giratoires', 'Virages · autoroute · départ sur route'] },
    ],
  },
  {
    id: 'passerelle',
    title: 'Passerelle A2 → A',
    subtitle: 'Consolider son expérience et accéder à une nouvelle moto',
    phase: '7 h · Expérience · Maîtrise · Autonomie',
    color: '#ea580c', // orange-600
    icon: Award,
    route: '/passerelle-a2-a-progression',
    ctaLabel: 'Ouvrir la trame Passerelle',
    steps: [
      { code: '1', title: 'Théorie · ≈ 2 h',           icon: Building2, items: ['Rappels administratifs & équipements', 'Retour d’expérience A2', 'Projection conduite moto A', 'Accidentologie & accidents-types'] },
      { code: '2', title: 'Hors circulation · ≈ 2 h',  icon: MapIcon,   items: ['Allure très lente & braquage', 'Gymkhana', 'Vérification du rétrogradage', 'Freinage d’urgence — 1 et 2 freins', 'Contre-braquage (guidon, jambes, bassin)', 'Maniabilité à 30 km/h · évitement'] },
      { code: '3', title: 'En circulation · ≈ 3 h',    icon: Bike,      items: ['Prise en main moto catégorie A', 'Roulage par tour de rôle · 1 h par motard', 'Observation par les autres stagiaires', 'Débriefing collectif · correction ciblée', 'Conduite autonome (≈ 30 min)'] },
    ],
  },
];

const ICONS_5Q = { pourquoi: HelpCircle, comment: GraduationCap, risques: AlertTriangle, influences: Wind, auto: CheckCircle2 };
const TONES_5Q = { pourquoi: 'text-sky-300', comment: 'text-sky-300', risques: 'text-rose-300', influences: 'text-amber-300', auto: 'text-emerald-300' };

function FormationCard({ f }) {
  const Icon = f.icon;
  return (
    <article className="rounded-3xl bg-[#141416] border border-white/10 overflow-hidden hover:border-white/25 transition group print:break-inside-avoid" data-testid={`formation-card-${f.id}`}>
      {/* Header couleur */}
      <div className="p-5 sm:p-6 border-b border-white/10" style={{ background: `linear-gradient(135deg, ${f.color}22 0%, transparent 60%)` }}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${f.color}25`, border: `1px solid ${f.color}55` }}>
            <Icon className="w-6 h-6" style={{ color: f.color }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-white text-xl sm:text-2xl font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{f.title}</h3>
            <p className="text-white/70 text-[13px] mt-1 leading-relaxed">{f.subtitle}</p>
            <div className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-black uppercase tracking-widest" style={{ color: f.color }}>
              <Clock className="w-3 h-3" /> {f.phase}
            </div>
          </div>
        </div>
      </div>

      {/* 3 étapes */}
      <div className="p-5 sm:p-6 space-y-3">
        {f.steps.map(s => {
          const SIcon = s.icon;
          return (
            <div key={s.code} className="rounded-xl bg-black/30 border border-white/5 p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-6 h-6 rounded-full text-white text-[11px] font-black flex items-center justify-center shrink-0" style={{ background: f.color }}>{s.code}</span>
                {SIcon && <SIcon className="w-3.5 h-3.5" style={{ color: f.color }} />}
                <h4 className="text-white font-black text-[13px] leading-tight">{s.title}</h4>
              </div>
              <ul className="grid sm:grid-cols-2 gap-x-3 gap-y-0.5">
                {s.items.map((it, i) => (
                  <li key={i} className="text-white/70 text-[12px] leading-relaxed flex gap-1.5">
                    <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full" style={{ background: f.color }} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        {/* CTA */}
        <Link
          to={f.route}
          className="mt-1 flex items-center justify-between gap-2 rounded-xl px-4 py-3 text-white font-black uppercase tracking-widest text-[12px] transition group/cta"
          style={{ background: f.color }}
          data-testid={`cta-${f.id}`}
        >
          <span>{f.ctaLabel}</span>
          <ArrowRight className="w-4 h-4 transition group-hover/cta:translate-x-1" />
        </Link>
      </div>
    </article>
  );
}

export default function Formations2Roues() {
  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white pb-24 lg:pb-16" data-testid="formations-2-roues">
      {/* Breadcrumb */}
      <div className="border-b border-white/10 bg-black/40 print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 text-[12px] text-white/50 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-white"><Home className="w-3.5 h-3.5 inline" /></Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white/80">Trames pédagogiques formations 2-roues</span>
        </div>
      </div>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[10px] font-black uppercase tracking-widest mb-3">
              <BookOpenCheck className="w-3 h-3" /> Référentiel enseignant · Utilisable en salle · plateau · circulation
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Trames pédagogiques<br className="hidden sm:block" /> formations 2-roues
            </h1>
            <p className="text-white/70 text-[14px] sm:text-[15px] mt-3 leading-relaxed">
              Une progression par compétences, adaptable à chaque élève. Comprendre · Progresser · Rouler en confiance.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#141416] px-4 py-3 text-right">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50 mb-0.5">Baseline commune</div>
            <div className="text-rose-300 font-mono text-[11.5px] sm:text-[12.5px]">{METHODE_BASELINE}</div>
          </div>
        </div>

        <p className="text-white/60 text-[13.5px] leading-relaxed max-w-4xl italic">{OUVERTURE_REFERENTIEL}</p>

        {/* 5 questions permanentes */}
        <div className="mt-6 rounded-2xl bg-[#141416] border border-white/10 p-4 sm:p-5">
          <div className="text-[10.5px] font-black uppercase tracking-widest text-white/50 mb-3">Méthode pédagogique commune — 5 questions permanentes</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {CINQ_QUESTIONS.map(q => {
              const I = ICONS_5Q[q.key];
              return (
                <div key={q.key} className="rounded-xl bg-black/40 border border-white/10 p-3">
                  <div className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest mb-1 ${TONES_5Q[q.key]}`}>
                    {I && <I className="w-3 h-3" />} {q.label}
                  </div>
                  <div className="text-white/70 text-[11.5px] leading-relaxed">{q.text}</div>
                </div>
              );
            })}
          </div>
        </div>
      </header>

      {/* 3 formations */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-3 gap-4 sm:gap-5">
          {FORMATIONS.map(f => <FormationCard key={f.id} f={f} />)}
        </div>
      </section>

      {/* Grille pédagogique par compétence */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-10 sm:mt-14">
        <div className="rounded-3xl bg-gradient-to-br from-rose-950/20 via-[#141416] to-[#141416] border border-white/10 p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center shrink-0">
              <BookOpenCheck className="w-5 h-5 text-rose-300" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Grille pédagogique par compétence</h2>
              <p className="text-white/70 text-[13.5px] mt-1">Chaque fiche exercice suit cette structure — utilisable telle quelle par l’enseignant.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { icon: Target,        title: '🎯 Objectif',           desc: 'Ce que l’élève doit être capable de réaliser.' },
              { icon: HelpCircle,    title: 'Pourquoi ?',             desc: 'Le sens de la compétence et son utilité en conduite.' },
              { icon: GraduationCap, title: 'Comment ?',              desc: 'Les actions techniques à mettre en œuvre.' },
              { icon: Bike,          title: '🏍️ Exercices',           desc: 'Situations proposées par l’enseignant, du plus simple au plus complexe.' },
              { icon: Eye,           title: '👀 Observation',         desc: "Indicateurs permettant de savoir où se situe la difficulté." },
              { icon: AlertTriangle, title: '⚠️ Risques',             desc: 'Ce qui peut provoquer une perte de maîtrise.' },
              { icon: Wind,          title: '🌦️ Influences',          desc: 'Élève / moto / environnement / situation.' },
              { icon: Wrench,        title: '🔧 Remédiation',         desc: 'Comment modifier l’exercice si l’élève rencontre une difficulté.' },
              { icon: MessageCircle, title: '🗣️ Questions',            desc: 'Faire réfléchir l’élève plutôt que lui donner la réponse.' },
              { icon: CheckCircle2,  title: '✅ Critère de réussite', desc: 'Ce qui permet de considérer la compétence comme stabilisée.' },
            ].map((c, i) => {
              const I = c.icon;
              return (
                <div key={i} className="rounded-xl bg-black/30 border border-white/5 p-3">
                  <div className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest text-white mb-1">
                    <I className="w-3.5 h-3.5 text-rose-300" /> {c.title}
                  </div>
                  <p className="text-white/65 text-[12px] leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6 niveaux de progression */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-8 sm:mt-10">
        <div className="rounded-3xl bg-[#141416] border border-white/10 p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-emerald-300" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Progression de difficulté — 6 niveaux</h2>
              <p className="text-white/70 text-[13.5px] mt-1">Applicable à chaque exercice, du plus guidé au plus autonome.</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {NIVEAUX_PROGRESSION.map(n => (
              <div key={n.n} className="rounded-xl bg-black/30 border border-white/5 p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-7 h-7 rounded-full text-white text-[12px] font-black flex items-center justify-center shrink-0" style={{ background: n.color }}>{n.n}</span>
                  <h4 className="text-white font-black text-[13.5px]">{n.code}</h4>
                </div>
                <p className="text-white/70 text-[12.5px] leading-relaxed">{n.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Logique de remédiation */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-8 sm:mt-10">
        <div className="rounded-3xl bg-gradient-to-br from-amber-950/20 via-[#141416] to-[#141416] border border-amber-500/20 p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
              <Wrench className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Logique de remédiation</h2>
              <p className="text-white/70 text-[13.5px] mt-1">Si l’élève échoue, on ne répète pas mécaniquement : on cherche la cause, on isole la difficulté, on complexifie progressivement.</p>
            </div>
          </div>

          <div className="rounded-2xl bg-black/30 border border-amber-500/20 p-4 mb-4">
            <div className="inline-flex items-center gap-1.5 text-[10.5px] font-black uppercase tracking-widest text-amber-200 mb-2"><Sparkles className="w-3 h-3" /> Règle d’or</div>
            <p className="text-amber-50 font-semibold text-[14px] leading-relaxed italic">{PRINCIPE_REMEDIATION.regleOr}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PRINCIPE_REMEDIATION.demarche.map((d, i) => (
              <div key={i} className="rounded-xl bg-black/30 border border-white/5 p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-6 h-6 rounded-full bg-amber-600 text-white text-[11px] font-black flex items-center justify-center shrink-0">{i + 1}</span>
                  <h4 className="text-white font-black text-[13.5px]">{d.etape}</h4>
                </div>
                <p className="text-white/70 text-[12.5px] leading-relaxed">{d.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bandeau final */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-10 sm:mt-14">
        <div className="rounded-3xl bg-gradient-to-br from-rose-950/30 via-[#141416] to-[#141416] border border-rose-500/20 p-6 sm:p-8 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/70 text-[10px] font-black uppercase tracking-widest mb-3">
            <Sparkles className="w-3 h-3 text-rose-300" /> Le 2-roues, une formation pour la vie
          </div>
          <p className="text-white/80 text-[15px] sm:text-[16px] italic leading-relaxed max-w-3xl mx-auto" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            « Chaque élève est unique, chaque progression est différente, la sécurité reste notre priorité. »
          </p>
        </div>
      </section>
    </div>
  );
}
